import React from 'react';
import { ShieldCheckIcon, AlertTriangleIcon, DownloadIcon, UsersIcon, CheckCircleIcon, ListChecksIcon } from 'lucide-react';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
export function PTPSummaryScreen({
  data,
  onBack,
  onNext,
  onExport
}: ScreenProps) {
  return <div className="flex flex-col h-full" data-id="element-325">
      <div className="flex items-center gap-3 mb-6" data-id="element-326">
        <ShieldCheckIcon className="w-8 h-8 text-orange-500" data-id="element-327" />
        <h2 className="text-2xl font-bold text-white" data-id="element-328">Pre-Task Plan</h2>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-4 px-4 space-y-6" data-id="element-329">
        {/* Header Info */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3" data-id="element-330">
          <div className="grid grid-cols-2 gap-4" data-id="element-331">
            <div data-id="element-332">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-333">
                Project
              </p>
              <p className="text-white font-medium text-sm" data-id="element-334">{data.project}</p>
            </div>
            <div data-id="element-335">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-336">
                Date
              </p>
              <p className="text-white font-medium text-sm" data-id="element-337">{data.date}</p>
            </div>
          </div>
          <div data-id="element-338">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-339">
              Location
            </p>
            <p className="text-white font-medium text-sm" data-id="element-340">{data.location}</p>
          </div>
          <div className="pt-3 border-t border-slate-800" data-id="element-341">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1" data-id="element-342">
              Task
            </p>
            <p className="text-orange-400 font-bold" data-id="element-343">
              Steel Beam Installation Using Mobile Crane
            </p>
          </div>
        </div>

        {/* Step-Based Hazard Analysis */}
        <section data-id="element-344">
          <div className="flex items-center gap-2 mb-3" data-id="element-345">
            <ListChecksIcon className="w-5 h-5 text-slate-400" data-id="element-346" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-347">
              Job Steps — Hazards & Controls
            </h3>
          </div>
          <div className="space-y-3" data-id="element-348">
            {data.steps.map((step, i) => <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-id="element-349">
                {/* Step header */}
                <div className="px-4 py-3 border-b border-slate-800 flex gap-3" data-id="element-350">
                  <span className="text-orange-500 font-bold text-sm flex-shrink-0" data-id="element-351">
                    {i + 1}.
                  </span>
                  <span className="text-white font-medium text-sm" data-id="element-352">
                    {step.description}
                  </span>
                </div>
                {/* Hazards & Controls for this step */}
                <div className="px-4 py-3 space-y-3" data-id="element-353">
                  {step.hazards.map((h, j) => <div key={j} className="space-y-2" data-id="element-354">
                      <div className="flex items-start gap-2" data-id="element-355">
                        <AlertTriangleIcon className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" data-id="element-356" />
                        <div className="flex-1 flex items-center gap-2" data-id="element-357">
                          <span className="text-red-200 text-xs" data-id="element-358">
                            {h.hazard}
                          </span>
                          {h.riskLevel && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${h.riskLevel === 'HIGH' ? 'bg-red-500/20 text-red-400' : h.riskLevel === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' : 'bg-green-500/20 text-green-400'}`} data-id="element-359">
                              {h.riskLevel}
                            </span>}
                        </div>
                      </div>
                      <div className="ml-5 space-y-1" data-id="element-360">
                        {h.controls.map((c, k) => <div key={k} className="flex items-start gap-2 text-green-100 text-xs" data-id="element-361">
                            <CheckCircleIcon className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" data-id="element-362" />
                            <span data-id="element-363">{c}</span>
                          </div>)}
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>
        </section>

        {/* Crew */}
        <section data-id="element-364">
          <div className="flex items-center gap-2 mb-3" data-id="element-365">
            <UsersIcon className="w-5 h-5 text-slate-400" data-id="element-366" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-367">
              Crew ({data.crewSize})
            </h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-368">
            {data.crewRoles.map((role, i) => <div key={i} className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-369">
                {role.role}
              </div>)}
          </div>
        </section>

        {/* PPE */}
        <section data-id="element-370">
          <div className="flex items-center gap-2 mb-3" data-id="element-371">
            <ShieldCheckIcon className="w-5 h-5 text-green-400" data-id="element-372" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider" data-id="element-373">
              Required PPE
            </h3>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-374">
            {data.ppeControls.map((ppe, i) => <div key={i} className="bg-green-950/20 border border-green-900/30 text-green-100 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-375">
                {ppe}
              </div>)}
          </div>
        </section>

        {/* Equipment */}
        <section data-id="element-376">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" data-id="element-377">
            Equipment
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-378">
            {data.equipment.map((item, i) => <div key={i} className="bg-slate-800 border border-slate-700 text-slate-300 px-3 py-1.5 rounded-md text-sm font-medium" data-id="element-379">
                {item}
              </div>)}
          </div>
        </section>

        {/* Safety Checks */}
        <section data-id="element-380">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3" data-id="element-381">
            Pre-Work Safety Checks
          </h3>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2" data-id="element-382">
            {data.safetyChecks.map((check, i) => <div key={i} className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-383">
                <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-384" />
                {check}
              </div>)}
          </div>
        </section>
      </div>

      <div className="pt-4 space-y-3 bg-slate-950" data-id="element-385">
        <PrimaryButton onClick={onNext} icon={<ShieldCheckIcon className="w-5 h-5" data-id="element-387" />} data-id="element-386">
          Finalize & Continue
        </PrimaryButton>
        <SecondaryButton onClick={() => onExport?.()} icon={<DownloadIcon className="w-5 h-5" data-id="element-389" />} data-id="element-388">
          Export PTP
        </SecondaryButton>
        <SecondaryButton onClick={onBack} data-id="element-390">Edit Plan</SecondaryButton>
      </div>
    </div>;
}